import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const LOGO_PATH = `${process.env.PUBLIC_URL || ""}/yavola_logo.png`;
const PAGE_WIDTH = 595.28; // A4 width in points
const PAGE_HEIGHT = 841.89; // A4 height in points
const PAGE_MARGIN = 56;
const BODY_FONT_SIZE = 12;
const HEADING_FONT_SIZE = 18;
const SUBHEADING_FONT_SIZE = 14;
const LINE_HEIGHT = BODY_FONT_SIZE * 1.5;
const PARAGRAPH_SPACING = LINE_HEIGHT * 0.75;

let cachedLogoBytes = null;

const ensureTrimmedString = (value) =>
  typeof value === "string" ? value.trim() : "";

const formatPriceDisplay = (value) => {
  const stringValue = ensureTrimmedString(value);

  if (stringValue) {
    return stringValue;
  }

  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : Number.NaN;

  if (!Number.isNaN(numericValue)) {
    try {
      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(numericValue);
      return `N$ ${formatted}`;
    } catch (error) {
      console.error("Failed to format price for booking confirmation", value, error);
      return `N$ ${numericValue}`;
    }
  }

  return "N$ —";
};

const formatDateDisplay = (displayValue, isoValue) => {
  const trimmed = ensureTrimmedString(displayValue);

  if (trimmed) {
    return trimmed;
  }

  const isoString = ensureTrimmedString(isoValue);

  if (!isoString) {
    return "—";
  }

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return isoString;
  }

  try {
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (error) {
    console.error("Failed to format booking confirmation date", isoString, error);
    return isoString;
  }
};

const deriveFileDateSegment = (isoValue) => {
  const isoString = ensureTrimmedString(isoValue);

  if (!isoString) {
    return "confirmation";
  }

  const date = new Date(isoString);

  if (Number.isNaN(date.getTime())) {
    return "confirmation";
  }

  return date.toISOString().slice(0, 10);
};

const normaliseDetails = (details = {}) => {
  const guestName = ensureTrimmedString(details.guestName || details.name) || "Guest";
  const bookingDateIso = ensureTrimmedString(
    details.bookingDateIso || details.bookingDate
  );
  const bookingDateDisplay = formatDateDisplay(
    details.bookingDateDisplay,
    bookingDateIso
  );
  const pickupPointDisplay =
    ensureTrimmedString(details.pickupPointDisplay || details.pickupPoint) ||
    "To be arranged";
  const totalPriceDisplay = formatPriceDisplay(
    details.totalPriceDisplay ?? details.totalPriceValue ?? details.totalPrice
  );

  const dateSegment = deriveFileDateSegment(bookingDateIso);
  const nameSegment = guestName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "guest";

  const fileName = `yavola-booking-${dateSegment}-${nameSegment}.pdf`;

  return {
    guestName,
    bookingDateIso,
    bookingDateDisplay,
    pickupPointDisplay,
    totalPriceDisplay,
    fileName,
  };
};

const fetchLogoBytes = async () => {
  if (cachedLogoBytes) {
    return cachedLogoBytes;
  }

  if (typeof fetch !== "function") {
    return null;
  }

  try {
    const response = await fetch(LOGO_PATH);

    if (!response.ok) {
      throw new Error(`Failed to fetch logo: ${response.status}`);
    }

    cachedLogoBytes = await response.arrayBuffer();
    return cachedLogoBytes;
  } catch (error) {
    console.error("Unable to load Yavola logo for PDF", error);
    return null;
  }
};

const wrapText = (text, font, size, maxWidth) => {
  const words = text.split(/\s+/).filter(Boolean);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    const tentativeLine = currentLine ? `${currentLine} ${word}` : word;
    const tentativeWidth = font.widthOfTextAtSize(tentativeLine, size);

    if (tentativeWidth <= maxWidth) {
      currentLine = tentativeLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

const drawParagraph = (page, text, { x, y, font, size, maxWidth, color }) => {
  const lines = wrapText(text, font, size, maxWidth);
  let cursorY = y;

  lines.forEach((line) => {
    page.drawText(line, {
      x,
      y: cursorY,
      size,
      font,
      color,
    });
    cursorY -= LINE_HEIGHT;
  });

  return cursorY;
};

const escapeHtml = (value) =>
  ensureTrimmedString(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const uint8ToBase64 = (uint8Array) => {
  if (!(uint8Array instanceof Uint8Array) || uint8Array.length === 0) {
    return "";
  }

  if (typeof Buffer === "function" && Buffer.from) {
    try {
      return Buffer.from(uint8Array).toString("base64");
    } catch (error) {
      console.error("Failed to encode confirmation PDF via Buffer", error);
    }
  }

  let binary = "";
  const chunkSize = 0x8000;

  for (let index = 0; index < uint8Array.length; index += chunkSize) {
    const chunk = uint8Array.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }

  if (typeof btoa === "function") {
    return btoa(binary);
  }

  throw new Error("Base64 encoding is not supported in this environment");
};

const base64ToUint8 = (base64) => {
  if (typeof base64 !== "string" || !base64) {
    return null;
  }

  const trimmed = base64.trim();

  if (!trimmed) {
    return null;
  }

  if (typeof Buffer === "function" && Buffer.from) {
    try {
      return Uint8Array.from(Buffer.from(trimmed, "base64"));
    } catch (error) {
      console.error("Failed to decode confirmation PDF via Buffer", error);
    }
  }

  if (typeof atob === "function") {
    const binary = atob(trimmed);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
  }

  console.error("Base64 decoding is not supported in this environment");
  return null;
};

export const buildBookingConfirmationEmailContent = (details = {}) => {
  const normalized = normaliseDetails(details);

  const html = `<!DOCTYPE html><html><body style="font-family: Arial, sans-serif; color: #1f2a2f; line-height: 1.6;">
    <p>Hello ${escapeHtml(normalized.guestName)},</p>
    <p>We are very excited about your upcoming journey with Yavola Tours and Transfers, and it will be our pleasure to welcome you on ${escapeHtml(
      normalized.bookingDateDisplay
    )}.</p>
    <p>To ensure your experience is outstanding, we’d like to reconfirm your reservation details:</p>
    <h3 style="color: #1c611c;">Reservation Details</h3>
    <ul style="list-style: none; padding: 0; margin: 0 0 16px 0;">
      <li><strong>Booking Date:</strong> ${escapeHtml(
        normalized.bookingDateDisplay
      )}</li>
      <li><strong>Pickup Point:</strong> ${escapeHtml(
        normalized.pickupPointDisplay
      )}</li>
      <li><strong>Total Price:</strong> ${escapeHtml(
        normalized.totalPriceDisplay
      )}</li>
    </ul>
    <h3 style="color: #1c611c;">Important Information About Etosha National Park</h3>
    <p>Please note that entrance fees to Etosha National Park are not included in your booking.</p>
    <ul style="list-style: none; padding: 0; margin: 0 0 16px 0;">
      <li>🚯 Do not litter</li>
      <li>🛍️ No plastic bags allowed</li>
      <li>🚁 No drones allowed</li>
      <li>🚗 Stay inside your vehicle at all times, except in designated areas</li>
    </ul>
    <p>Our mission at Yavola is to create unforgettable experiences through personalized service and deep local knowledge.</p>
    <p>Thank you,<br/>The Yavola Team</p>
    <p style="font-size: 0.9rem; color: #54616a;">Your detailed confirmation is attached as a PDF for easy reference.</p>
  </body></html>`;

  const text = [
    `Hello ${normalized.guestName},`,
    "",
    `We are very excited about your upcoming journey with Yavola Tours and Transfers, and it will be our pleasure to welcome you on ${normalized.bookingDateDisplay}.`,
    "",
    "To ensure your experience is outstanding, we’d like to reconfirm your reservation details:",
    "",
    "Reservation Details",
    `Booking Date: ${normalized.bookingDateDisplay}`,
    `Pickup Point: ${normalized.pickupPointDisplay}`,
    `Total Price: ${normalized.totalPriceDisplay}`,
    "",
    "Important Information About Etosha National Park",
    "Please note that entrance fees to Etosha National Park are not included in your booking.",
    "- 🚯 Do not litter",
    "- 🛍️ No plastic bags allowed",
    "- 🚁 No drones allowed",
    "- 🚗 Stay inside your vehicle at all times, except in designated areas",
    "",
    "Our mission at Yavola is to create unforgettable experiences through personalized service and deep local knowledge.",
    "",
    "Thank you,",
    "The Yavola Team",
    "",
    "Your detailed confirmation is attached as a PDF for easy reference.",
  ].join("\n");

  return { html, text, normalized };
};

export const createBookingConfirmationPdf = async (details = {}) => {
  const { normalized } = buildBookingConfirmationEmailContent(details);

  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([PAGE_WIDTH, PAGE_HEIGHT]);
  const bodyFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let cursorY = PAGE_HEIGHT - PAGE_MARGIN;
  const contentWidth = PAGE_WIDTH - PAGE_MARGIN * 2;

  try {
    const logoBytes = await fetchLogoBytes();

    if (logoBytes) {
      try {
        const logoImage = await pdfDoc.embedPng(logoBytes);
        const logoWidth = 150;
        const logoHeight = (logoWidth / logoImage.width) * logoImage.height;

        page.drawImage(logoImage, {
          x: PAGE_MARGIN,
          y: cursorY - logoHeight,
          width: logoWidth,
          height: logoHeight,
        });
        cursorY -= logoHeight + PARAGRAPH_SPACING;
      } catch (embedError) {
        console.error("Failed to embed Yavola logo in confirmation PDF", embedError);
      }
    }
  } catch (logoError) {
    console.error("Unexpected error loading logo for confirmation PDF", logoError);
  }

  page.drawText("Booking Confirmation", {
    x: PAGE_MARGIN,
    y: cursorY,
    size: HEADING_FONT_SIZE,
    font: boldFont,
    color: rgb(0.11, 0.38, 0.11),
  });
  cursorY -= LINE_HEIGHT * 1.5;

  cursorY = drawParagraph(`Hello ${normalized.guestName},`, {
    x: PAGE_MARGIN,
    y: cursorY,
    font: bodyFont,
    size: BODY_FONT_SIZE,
    maxWidth: contentWidth,
    color: rgb(0.12, 0.16, 0.18),
  }) - PARAGRAPH_SPACING;

  cursorY = drawParagraph(
    `We are very excited about your upcoming journey with Yavola Tours and Transfers, and it will be our pleasure to welcome you on ${normalized.bookingDateDisplay}.`,
    {
      x: PAGE_MARGIN,
      y: cursorY,
      font: bodyFont,
      size: BODY_FONT_SIZE,
      maxWidth: contentWidth,
      color: rgb(0.12, 0.16, 0.18),
    }
  ) - PARAGRAPH_SPACING;

  cursorY = drawParagraph(
    "To ensure your experience is outstanding, we’d like to reconfirm your reservation details:",
    {
      x: PAGE_MARGIN,
      y: cursorY,
      font: bodyFont,
      size: BODY_FONT_SIZE,
      maxWidth: contentWidth,
      color: rgb(0.12, 0.16, 0.18),
    }
  ) - LINE_HEIGHT;

  page.drawText("Reservation Details", {
    x: PAGE_MARGIN,
    y: cursorY,
    size: SUBHEADING_FONT_SIZE,
    font: boldFont,
    color: rgb(0.11, 0.38, 0.11),
  });
  cursorY -= LINE_HEIGHT;

  const reservationDetails = [
    ["Booking Date:", normalized.bookingDateDisplay],
    ["Pickup Point:", normalized.pickupPointDisplay],
    ["Total Price:", normalized.totalPriceDisplay],
  ];

  const labelWidth = Math.max(
    ...reservationDetails.map(([label]) =>
      boldFont.widthOfTextAtSize(label, BODY_FONT_SIZE)
    )
  );

  reservationDetails.forEach(([label, value]) => {
    page.drawText(label, {
      x: PAGE_MARGIN,
      y: cursorY,
      size: BODY_FONT_SIZE,
      font: boldFont,
      color: rgb(0.12, 0.16, 0.18),
    });
    page.drawText(value, {
      x: PAGE_MARGIN + labelWidth + 8,
      y: cursorY,
      size: BODY_FONT_SIZE,
      font: bodyFont,
      color: rgb(0.12, 0.16, 0.18),
    });
    cursorY -= LINE_HEIGHT;
  });

  cursorY -= PARAGRAPH_SPACING / 2;

  page.drawText("Important Information About Etosha National Park", {
    x: PAGE_MARGIN,
    y: cursorY,
    size: SUBHEADING_FONT_SIZE,
    font: boldFont,
    color: rgb(0.11, 0.38, 0.11),
  });
  cursorY -= LINE_HEIGHT;

  cursorY = drawParagraph(
    "Please note that entrance fees to Etosha National Park are not included in your booking.",
    {
      x: PAGE_MARGIN,
      y: cursorY,
      font: bodyFont,
      size: BODY_FONT_SIZE,
      maxWidth: contentWidth,
      color: rgb(0.12, 0.16, 0.18),
    }
  ) - PARAGRAPH_SPACING / 2;

  const parkRules = [
    "🚯 Do not litter",
    "🛍️ No plastic bags allowed",
    "🚁 No drones allowed",
    "🚗 Stay inside your vehicle at all times, except in designated areas",
  ];

  parkRules.forEach((rule) => {
    page.drawText(rule, {
      x: PAGE_MARGIN,
      y: cursorY,
      size: BODY_FONT_SIZE,
      font: bodyFont,
      color: rgb(0.12, 0.16, 0.18),
    });
    cursorY -= LINE_HEIGHT;
  });

  cursorY -= PARAGRAPH_SPACING / 2;

  cursorY = drawParagraph(
    "Our mission at Yavola is to create unforgettable experiences through personalized service and deep local knowledge.",
    {
      x: PAGE_MARGIN,
      y: cursorY,
      font: bodyFont,
      size: BODY_FONT_SIZE,
      maxWidth: contentWidth,
      color: rgb(0.12, 0.16, 0.18),
    }
  ) - PARAGRAPH_SPACING;

  page.drawText("Thank you,", {
    x: PAGE_MARGIN,
    y: cursorY,
    size: BODY_FONT_SIZE,
    font: bodyFont,
    color: rgb(0.12, 0.16, 0.18),
  });
  cursorY -= LINE_HEIGHT;

  page.drawText("The Yavola Team", {
    x: PAGE_MARGIN,
    y: cursorY,
    size: BODY_FONT_SIZE,
    font: bodyFont,
    color: rgb(0.12, 0.16, 0.18),
  });

  let base64 = "";
  let byteArray = null;

  try {
    base64 = await pdfDoc.saveAsBase64({ dataUri: false });
    byteArray = base64ToUint8(base64);
  } catch (base64Error) {
    console.error(
      "Failed to export booking confirmation PDF as base64 directly",
      base64Error
    );
  }

  if (!byteArray) {
    const pdfBytes = await pdfDoc.save();
    byteArray = pdfBytes instanceof Uint8Array ? pdfBytes : new Uint8Array();

    if (!base64) {
      try {
        base64 = uint8ToBase64(byteArray);
      } catch (encodingError) {
        console.error(
          "Failed to encode booking confirmation PDF to base64",
          encodingError
        );
        base64 = "";
      }
    }
  }

  let blob = null;
  if (typeof Blob === "function" && byteArray?.length) {
    try {
      blob = new Blob([byteArray], { type: "application/pdf" });
    } catch (blobError) {
      console.error("Failed to build confirmation PDF blob", blobError);
    }
  }

  return {
    base64: typeof base64 === "string" ? base64.trim() : "",
    blob,
    fileName: normalized.fileName,
  };
};

export default createBookingConfirmationPdf;
